var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LoginSource } from "healthex-common/heap.dtos";
import { OTPClearLoginState, VerifyOTPState } from "./VerifyOTPManager";
import { determineOrgId, PatientConsentViewModel } from "./PatientConsentViewModel";
import { ClearAuthState } from "../Infra/Persistence";
import { ClearInitialScreen } from "./ClearAuthManager";
import { Disposer } from "../Infra/Disposer";
import { PatientConsentSessionState } from "./PatientConsentSession";
import { computed, observable } from "mobx";
import { getObservableT } from "../Infra/i18next";
const t = getObservableT("patients");
export class PatientConsentVerifyPhoneViewModel extends PatientConsentViewModel {
    constructor(container) {
        super(container);
        this.disposer = new Disposer();
        this.hasClearError = false;
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.verifyOTPManager = container.verifyOTPManager;
        this.persistence = container.persistence;
        this.clearAuthManager = container.clearAuthManager;
        this.loginUIManager = container.loginUIManager;
        this.socialLoginAuthManager = container.socialLoginAuthManager;
        this.accountSetupCompletionManager = container.accountSetupCompletionManager;
        this.bannerUIManager = container.bannerUIManager;
    }
    run() {
        this.verifyOTPManager.resetVerificationFlow();
        this.disposer.reaction(() => this.patientConsentUIManager.session.patientConsentSessionState, (state) => {
            if (state === PatientConsentSessionState.RESTORING_COMPLETED) {
                this.handleClearLoginResultIfNeeded();
            }
        }, { fireImmediately: true, name: "PatientConsentVerifyPhoneViewModel_clearAuthManagerState" });
    }
    stop() {
        this.disposer.dispose();
    }
    get formModel() {
        return this.verifyOTPManager.formModel;
    }
    get maskedPhoneNumber() {
        return this.verifyOTPManager.getMaskedPhoneNumber();
    }
    get verifyOTPState() {
        return this.verifyOTPManager.verifyOTPState;
    }
    get sendOTPState() {
        return this.verifyOTPManager.sendOTPState;
    }
    get resendCooldownSecondsLeft() {
        return this.verifyOTPManager.resendCooldownSecondsLeft;
    }
    get isSandboxLogin() {
        return this.persistence.isSandboxLogin();
    }
    get isLoggingIn() {
        return this.clearAuthManager.state === ClearAuthState.IN_PROGRESS;
    }
    get isClearLoginSuccess() {
        return this.clearAuthManager.state === ClearAuthState.SUCCESS;
    }
    get isClearLoggingIn() {
        return this.clearAuthManager.state === ClearAuthState.IN_PROGRESS;
    }
    get clearLoginState() {
        var _a;
        if (this.hasClearError)
            return OTPClearLoginState.CLEAR_ERROR;
        const isIAL2Expired = (_a = this.patientConsentUIManager.getIAL2Info()) === null || _a === void 0 ? void 0 : _a.ial2Expired;
        if (isIAL2Expired === true)
            return OTPClearLoginState.CLEAR_EXPIRED;
        if (isIAL2Expired === null)
            return OTPClearLoginState.LOADING;
        return OTPClearLoginState.DEFAULT;
    }
    async sendVerificationCode() {
        await this.verifyOTPManager.sendVerificationCode();
    }
    async submitVerificationCode() {
        const verified = await this.verifyOTPManager.submitVerificationCode();
        if (!verified) {
            return;
        }
        await this.completeVerification();
    }
    resetVerifyingStateAfterFailedCompletion() {
        if (this.verifyOTPManager.verifyOTPState === VerifyOTPState.VERIFYING) {
            this.verifyOTPManager.verifyOTPState = VerifyOTPState.INITIAL;
        }
    }
    async completeVerification() {
        const { input } = this.accountSetupCompletionManager;
        const result = await this.socialLoginAuthManager.completeVerificationFlow({
            firstName: input.firstName,
            lastName: input.lastName,
            dateOfBirth: input.birthday,
            phone: input.mobilePhone,
        }, () => {
            this.verifyOTPManager.pendingVerificationKey = null;
            this.verifyOTPManager.pendingVerificationPhone = null;
            this.patientConsentUIManager.goToNextPage();
        });
        if (result.ok) {
            this.accountSetupCompletionManager.trackMobileVerified(result.isFirstTimeLogin, result.gfidProvider);
            return;
        }
        if (result.code === "no_pending_data") {
            this.resetVerifyingStateAfterFailedCompletion();
            this.bannerUIManager.displayBanner({
                type: "ERROR",
                text: t("Something went wrong completing your login. Please try again."),
                durationInMS: 6000,
            });
        }
        this.socialLoginAuthManager.clearPendingSocialLogin();
        this.persistence.setConsentDeferredVerificationStash(null);
        this.verifyOTPManager.pendingVerificationKey = null;
        this.verifyOTPManager.pendingVerificationPhone = null;
        this.resetVerifyingStateAfterFailedCompletion();
        this.patientConsentUIManager.goToPreviousPage();
    }
    async tryADifferentPhoneNumber() {
        this.patientConsentUIManager.goToPreviousPage();
    }
    get initialScreen() {
        switch (this.verifyOTPManager.verifyOTPState) {
            case VerifyOTPState.LOCKOUT:
                return ClearInitialScreen.OTP_LOCKOUT;
            default:
                return ClearInitialScreen.OTP_VERIFY_PASSCODE;
        }
    }
    async verifyWithClear(isSandbox = false) {
        if (this.isScreenshotMode)
            return;
        this.patientConsentUIManager.setSelectedLoginType("CLEAR");
        if (this.patientConsentUIManager.showEmailCollectionPage) {
            this.persistence.setSandboxLogin(isSandbox);
            this.goToNextPage();
            return;
        }
        this.patientConsentUIManager.setHasLoggedInDuringConsent();
        this.persistence.setSandboxLogin(isSandbox);
        await this.patientConsentUIManager.loginToClearAndSaveState(window.location.hash, isSandbox, LoginSource.OTP_VERIFICATION_CONSENT, determineOrgId(this.loginUIManager, this.studyInfo));
    }
    handleClearLoginResultIfNeeded() {
        var _a;
        if (this.authManager.accountLinkingConflict) {
            this.patientConsentUIManager.completeStateRestoration();
            return;
        }
        if (this.clearAuthManager.state === ClearAuthState.EXITED) {
            this.clearAuthManager.resetAuth();
            this.patientConsentUIManager.completeStateRestoration();
            return;
        }
        if (((_a = this.patientConsentUIManager.getIAL2Info()) === null || _a === void 0 ? void 0 : _a.ial2Authorized) === true && this.isClearLoginSuccess) {
            const nextPageRoute = this.getNextPageRoute();
            if (nextPageRoute) {
                this.goToNextPageAndResetAuth(nextPageRoute);
            }
        }
        else {
            this.hasClearError = true;
        }
        this.patientConsentUIManager.completeStateRestoration();
    }
    getNextPageRoute() {
        var _a, _b;
        return (_b = (_a = this.clearAuthManager.getClearAuthData()) === null || _a === void 0 ? void 0 : _a.nextPageRoute) !== null && _b !== void 0 ? _b : null;
    }
    goToNextPageAndResetAuth(nextPageRoute) {
        if (nextPageRoute) {
            this.goToPage(nextPageRoute);
            this.clearAuthManager.resetAuth();
        }
    }
}
__decorate([
    computed
], PatientConsentVerifyPhoneViewModel.prototype, "isLoggingIn", null);
__decorate([
    computed
], PatientConsentVerifyPhoneViewModel.prototype, "isClearLoginSuccess", null);
__decorate([
    computed
], PatientConsentVerifyPhoneViewModel.prototype, "isClearLoggingIn", null);
__decorate([
    observable
], PatientConsentVerifyPhoneViewModel.prototype, "hasClearError", void 0);
__decorate([
    computed
], PatientConsentVerifyPhoneViewModel.prototype, "clearLoginState", null);
